/* eslint-disable jsx-a11y/control-has-associated-label */
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import logger from 'src/util/logger';

import TradeRuleListTableItem from './TradeRuleListTableItem';

class TradeRuleListTable extends React.Component {
  loadMore = () => {
    const { relay } = this.props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.log(error); },
    );
  };

  render() {
    if (this.error) {
      return <div><FormattedMessage id="error.title" defaultMessage="Error!" /></div>;
    }
    if (!this.props) {
      return <Loading />;
    }

    const {
      intl, relay, meter, propertyId,
    } = this.props;

    const trades = (!meter.rules || !meter.rules.edges)
      ? [] : meter.rules.edges.map((el) => (el.node));

    const clauseHeaderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_list.trade_rule_list_table.header.clauses.label', defaultMessage: 'Clauses' });

    const copyLoadMore = intl.formatMessage({ id: 'trade_rule_list.trade_rule_list_table.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
    const copyNoMore = intl.formatMessage({ id: 'trade_rule_list_table.load_more_button.state_no_more.label', defaultMessage: 'No more trade rules to load' });

    return (
      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col"><FormattedMessage id="trade_rule.trade_rule_list_table.head.id.label" defaultMessage="ID" /></th>
                <th scope="col"><FormattedMessage id="trade_rule.trade_rule_list_table.head.type.label" defaultMessage="Type" /></th>
                <th scope="col"><FormattedMessage id="trade_rule.trade_rule_list_table.head.state.label" defaultMessage="State" /></th>
                <th scope="col"><FormattedMessage id="trade_rule.trade_rule_list_table.head.direction.label" defaultMessage="Direction" /></th>
                <th scope="col"><FormattedMessage id="trade_rule.trade_rule_list_table.head.counterparty_trader.label" defaultMessage="Counterparty Trader" /></th>
                <th scope="col"><FormattedMessage id="trade_rule.trade_rule_list_table.head.counterparty_trade_point.label" defaultMessage="Counterparty Trade Point" /></th>
                <th scope="col">{clauseHeaderLabel}</th>
                <th scope="col"><FormattedMessage id="trade_rule.trade_rule_list_table.head.validity.label" defaultMessage="Validity" /></th>
              </tr>
            </thead>
            <tbody>
              {trades
                && trades.map((tradeRule) => (
                  <TradeRuleListTableItem
                    tradePointId={meter.tradePointId}
                    rule={tradeRule}
                    propertyId={propertyId}
                    key={tradeRule.uuid}
                  />
                ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            hasNext={relay.hasMore()}
            onClick={this.loadMore}
          />
        </div>
      </div>
    );
  }
}

TradeRuleListTable.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  meter: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  intl: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  propertyId: PropTypes.string.isRequired,
};

export default injectIntl(createPaginationContainer(
  TradeRuleListTable,
  {
    meter: graphql`
      fragment TradeRuleListTable_meter on Meter @argumentDefinitions(
        first: { type: "Int"}
        after: { type: "String" }
        last: { type: "Int"}
        before: { type: "String" }
      ) {
        uuid
        identifier
        title
        tradePointId
        active {
          start
          finish
        }
        rules(
          after: $after
          before: $before
          first: $first
          last: $last
        ) @connection(key: "TradeRuleListTable_rules") {
          edges {
            cursor
            node {
              uuid
              ...TradeRuleListTableItem_rule
            }
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.meter.rules;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
        uuid: props.meter.uuid,
      };
    },
    query: graphql`
      # Pagination query to be fetched upon calling 'loadMore'.
      # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
      query TradeRuleListTable_Pagination_Query(
        $uuid: ID!
        $count: Int
        $cursor: String
      ) {
        meter(uuid: $uuid) {
          ...TradeRuleListTable_meter @arguments(
            first: $count
            after: $cursor
          )
        }
      }
    `,
  },
));
